import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StepCard from './StepCard';
import { Form, Upload, Alert, Spin, notification } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CloseOutlined } from '@ant-design/icons';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useIntl } from 'react-intl';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import { VerifiedIdOrCodeStatusEnum } from '@nxsys/constants/AppEnums';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { showMessage } from '@nxsys/utils/message';
import { getBackendMessage } from '@nxsys/utils/getErrorMessage';
import styled from 'styled-components';
import StepStateContext from './StepStateContext';

// Company-configured UK ID verification method (companyPayment.UKVerifiedIdMethod).
// Anything else (incl. empty/null) falls through to the default Yoti passport
// verification widget.
const UK_VERIFIED_ID_METHOD = {
  MANUAL_VERIFICATION: 'MANUAL_VERIFICATION',
  ACCEPT_BIRTH_CERTIFICATE: 'ACCEPT_BIRTH_CERTIFICATE',
} as const;

// Yoti session states that count as a completed verification.
const YOTI_DONE_STATES = ['DONE', 'APPROVE', 'COMPLETED'];
// Yoti is still processing the session — keep polling.
const YOTI_ONGOING_STATE = 'ONGOING';
// Yoti session ran out before it completed.
const YOTI_EXPIRED_STATE = 'EXPIRED';
// Yoti session failed.
const YOTI_ERROR_STATE = 'ERROR';
// Sessions in these states cannot complete — the employee has to verify again.
const YOTI_FAILED_STATES = [YOTI_EXPIRED_STATE, YOTI_ERROR_STATE];
const YOTI_POLL_INTERVAL_MS = 30_000;
// Consecutive failed polls after which polling gives up.
const YOTI_POLL_MAX_FAILURES = 3;
// Yoti widget ERROR event codes that should close the modal.
const YOTI_ERROR_CLOSE_CODES = [2000, 2001, 2002, 2003, 2004, 3000];
// Yoti widget code sent when the user closes the widget (USER_CANCELLED).
const YOTI_USER_CANCELLED_CODE = 1000;

// Full-screen, see-through overlay while Yoti processes a session — same look
// as the employee onboarding page's own loader.
const YotiPollingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: rgba(255, 255, 255, 0.6);
`;

interface AddIDProps {
  stepNumber: number;
  documentCopyBirthcertificate?: string | null;
  havePassport?: boolean;
  verifiedIdOrCodeStatus?: VerifiedIdOrCodeStatusEnum;
  ukVerifiedIdMethod?: string | null;
  /** Id and state of the latest Yoti session, as tracked by the backend. */
  yotiSessionId?: string | null;
  yotiSessionState?: string | null;
  /** Reloads the onboarding so the Yoti session fields above are current. */
  onRefetch?: () => void;
  /**
   * Greys out the Yoti "Verify ID" button. The widget is a live camera check on
   * the employee's own device, so an admin completing the onboarding for a
   * prospect cannot run it — same reasoning as the Zoho button in VerifyContact.
   */
  disabledVerify?: boolean;
  completed?: boolean;
  /**
   * The whole onboarding has succeeded, so it is read-only: a step save would
   * be refused, so a Yoti session is neither resumed nor submitted.
   */
  onboardingCompleted?: boolean;
  onSubmitStep: (
    values: any,
    options?: { hideToast?: boolean; skipComplete?: boolean },
  ) => void;
}

type AddIDValues = {
  documentIdValue?: UploadFile[];
  havePassportValue?: boolean;
};

const normFile = (e: any): UploadFile[] => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList ?? [];
};

const AddID: React.FC<AddIDProps> = ({
  stepNumber,
  documentCopyBirthcertificate,
  havePassport,
  verifiedIdOrCodeStatus,
  ukVerifiedIdMethod,
  yotiSessionId,
  yotiSessionState,
  onRefetch,
  disabledVerify = false,
  completed = false,
  onboardingCompleted = false,
  onSubmitStep,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm<AddIDValues>();
  const { messages } = useIntl();
  const watchHavePassport = Form.useWatch('havePassportValue', form);
  // Ticking the checkbox saves on its own, and the manual-verification card has
  // no Save button, so the checkbox is the only place that save can show
  // progress.
  const { submitting } = useContext(StepStateContext);
  const isDone = !!(verifiedIdOrCodeStatus === VerifiedIdOrCodeStatusEnum.DONE);

  // With a passport → the ID is verified through the Yoti widget.
  // Without a passport (UK) → the company's UKVerifiedIdMethod decides the
  // fallback: upload a birth certificate, or manual verification (status card).
  const isAcceptBirthCertificate =
    ukVerifiedIdMethod === UK_VERIFIED_ID_METHOD.ACCEPT_BIRTH_CERTIFICATE;

  // Covers the S3 upload in onFinish, which happens before onSubmitStep and so
  // is not covered by the onboarding modal's loader.
  const [saving, setSaving] = useState(false);

  // ===== Yoti widget state =====
  const [yotiUrl, setYotiUrl] = useState<string | null>(null);
  const [yotiLoading, setYotiLoading] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailuresRef = useRef(0);
  // Mirrors pollRef so the processing status, the full-screen loader and the
  // Verify ID button can render off it.
  const [yotiPolling, setYotiPolling] = useState(false);
  // Set once a poll in this page answers EXPIRED/ERROR, or cleared ('') when a
  // new session is started; until then (null) the backend's yotiSessionState
  // decides.
  const [yotiFailedState, setYotiFailedState] = useState<string | null>(null);
  // Yoti answered COMPLETED in this page — shown as verified straight away,
  // before the step save comes back with the DONE status.
  const [yotiCompleted, setYotiCompleted] = useState(false);
  // The backend's Yoti session state only applies to a passport holder who
  // has actually started a Yoti session.
  const hasYotiSession = havePassport === true && !!yotiSessionId;
  const backendYotiState = hasYotiSession
    ? String(yotiSessionState ?? '').toUpperCase()
    : '';
  // The employee is mid-way through a Yoti check — an admin must not change
  // the step under them, so every control is locked until it settles.
  const lockedForAdmin =
    disabledVerify && !isDone && backendYotiState === YOTI_ONGOING_STATE;
  const isVerified =
    isDone || yotiCompleted || YOTI_DONE_STATES.includes(backendYotiState);
  const failedYotiState = isDone
    ? ''
    : (yotiFailedState ??
      (YOTI_FAILED_STATES.includes(backendYotiState) ? backendYotiState : ''));
  // Concurrent polls, or the backend state plus a poll, can all report
  // COMPLETED — submit the step once.
  const verifiedRef = useRef(false);
  // The poll interval keeps the closure it was started with — read the latest
  // refetch through a ref so it is never a stale or missing one.
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setYotiPolling(false);
  };

  const startPolling = () => {
    stopPolling();
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(pollYotiState, YOTI_POLL_INTERVAL_MS);
    setYotiPolling(true);
  };

  // Clear the poll timer if the component unmounts mid-verification.
  useEffect(() => stopPolling, []);

  const closeYotiModal = () => {
    setYotiUrl(null);
    // Polling only runs once the user has finished the widget, and that session
    // keeps polling in the background — closing the modal must not throw its
    // result away.
    if (!pollRef.current) sessionIdRef.current = null;
  };

  const dropYotiSession = () => {
    stopPolling();
    sessionIdRef.current = null;
    setYotiUrl(null);
  };

  // The user cancelled the processing overlay — stop polling and reload the
  // onboarding so the step reads what the backend records for the session.
  const cancelYotiSession = () => {
    dropYotiSession();
    onRefetchRef.current?.();
  };

  const handleYotiVerified = () => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;
    dropYotiSession();
    onSubmitStep({
      havePassport: true,
      verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.DONE,
    });
  };

  // COMPLETED → submit the step. ONGOING (or no readable state) → leave the
  // session tracked so polling carries on. Any other state (e.g. EXPIRED) means
  // the session can no longer complete → drop it.
  // Every check reloads the onboarding afterwards so the step (and the rest of
  // the page) reads what the backend now records for the session — except
  // COMPLETED, whose step save already reloads it.
  const pollYotiState = async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    let refetch = true;
    try {
      const res = await dispatch(
        appActions.checkStateVerifiedYoti({ sessionId }),
      ).unwrap();
      const state = String(
        res?.data?.state ?? res?.data?.data?.state ?? '',
      ).toUpperCase();
      // A response without a state (e.g. an error the backend answers with
      // 200) counts as a failed poll too — thrown as is so its message shows.
      if (!state) throw res;
      pollFailuresRef.current = 0;
      if (YOTI_DONE_STATES.includes(state)) {
        refetch = false;
        setYotiCompleted(true);
        handleYotiVerified();
      } else if (state !== YOTI_ONGOING_STATE) {
        if (YOTI_FAILED_STATES.includes(state)) setYotiFailedState(state);
        dropYotiSession();
      }
    } catch (error) {
      // Ride out a transient failure, but give up after a few in a row so the
      // processing state (and full-screen loader) cannot hang forever.
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= YOTI_POLL_MAX_FAILURES) {
        dropYotiSession();
        showMessage({
          type: 'error',
          content:
            getBackendMessage(error) ||
            ((messages['prospect.onboarding.yotiPollError'] ??
              "Couldn't confirm your ID verification. Please try again.") as string),
        });
      }
    } finally {
      if (refetch) onRefetchRef.current?.();
    }
  };

  const handleVerifyId = async () => {
    try {
      setYotiLoading(true);
      const res = await dispatch(appActions.getSignatureYoti()).unwrap();
      const payload = res?.data?.data ?? res?.data ?? {};
      const sessionId = payload.sessionId ?? payload.sessionID;
      const sessionToken = payload.clientSessionToken ?? payload.sessionToken;
      if (!sessionId || !sessionToken) {
        showMessage({
          type: 'error',
          content: (messages['prospect.onboarding.yotiVerifyError'] ??
            'Failed to start ID verification') as string,
        });
        return;
      }
      stopPolling();
      setYotiFailedState('');
      sessionIdRef.current = sessionId;
      // Open the widget; polling starts only after the widget posts a SUCCESS
      // message (see the message listener below), not on open.
      setYotiUrl(
        `https://api.yoti.com/idverify/v1/web/index.html?sessionID=${sessionId}&sessionToken=${sessionToken}`,
      );
    } catch {
      showMessage({
        type: 'error',
        content: (messages['prospect.onboarding.yotiVerifyError'] ??
          'Failed to start ID verification') as string,
      });
    } finally {
      setYotiLoading(false);
    }
  };

  // Listen to the Yoti widget's postMessage events while the modal is open:
  //  - SUCCESS → the user finished the flow → start polling the session state.
  //  - USER_CANCELLED → close the modal and let the user know they can restart.
  //  - ERROR (fatal codes) → close the modal.
  useEffect(() => {
    if (!yotiUrl) return;
    const onMessage = (event: MessageEvent) => {
      const eventType = event?.data?.eventType;
      const eventCode = event?.data?.eventCode;
      if (eventType === 'SUCCESS') {
        // The widget is finished with — the step card shows the processing
        // status from here.
        setYotiUrl(null);
        startPolling();
      } else if (Number(eventCode) === YOTI_USER_CANCELLED_CODE) {
        closeYotiModal();
        notification.info({
          message: (messages['prospect.onboarding.yotiCancelledTitle'] ??
            'Verification Cancelled') as string,
          description: (messages['prospect.onboarding.yotiCancelledDesc'] ??
            "You can restart the verification whenever you're ready.") as string,
        });
      } else if (
        eventType === 'ERROR' &&
        YOTI_ERROR_CLOSE_CODES.includes(eventCode)
      ) {
        closeYotiModal();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [yotiUrl]);

  // Pick up a Yoti session that was finished in the widget but whose result was
  // never picked up (tab/app closed while Yoti was still processing), using the
  // session state the backend reports on the onboarding:
  //  - COMPLETED → submit the step.
  //  - ONGOING   → resume polling that session, checking it straight away:
  //    the backend only records a new state when it is checked, so Yoti may
  //    well have finished while the page was closed.
  //  - anything else → the session can no longer complete, nothing to do.
  useEffect(() => {
    if (isDone || onboardingCompleted || disabledVerify || sessionIdRef.current)
      return;
    if (!hasYotiSession) return;
    if (YOTI_DONE_STATES.includes(backendYotiState)) {
      handleYotiVerified();
      return;
    }
    if (backendYotiState === YOTI_ONGOING_STATE) {
      sessionIdRef.current = yotiSessionId ?? null;
      startPolling();
      pollYotiState();
    }
  }, [
    isDone,
    onboardingCompleted,
    disabledVerify,
    yotiSessionId,
    backendYotiState,
  ]);

  const onFinish = async (values: AddIDValues) => {
    const havePassportValue = values.havePassportValue;
    // No passport: when the company accepts a birth certificate, upload it;
    // otherwise just mark the ID verification pending.
    if (!havePassportValue) {
      if (isAcceptBirthCertificate) {
        let url = null;
        // An already-saved document is seeded into the fileList as `{ name }`
        // with no `originFileObj`, so only upload when the user actually picked
        // a new file — otherwise keep the existing document.
        const newFile = values.documentIdValue?.[0]?.originFileObj as
          | File
          | undefined;
        if (newFile) {
          // The S3 upload runs before onSubmitStep, so it is outside the
          // modal's own loader — keep a local saving flag over both.
          try {
            setSaving(true);
            const res = await dispatch(
              commonActions.uploadFileToS3({ file: newFile }),
            ).unwrap();
            url = res?.url;
          } finally {
            setSaving(false);
          }
        }
        onSubmitStep({
          havePassport: false,
          documentCopyBirthcertificate: url ?? documentCopyBirthcertificate,
          verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.PENDING,
        });
        return;
      }
      onSubmitStep({
        havePassport: false,
        verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.PENDING,
      });
      return;
    }
    // Has passport (non-Yoti methods) → mark pending; the Yoti path submits
    // via the widget completion handler instead.
    onSubmitStep({
      havePassport: true,
      verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.PENDING,
    });
  };

  // Dedicated checkbox handler (parity with AddShareCode). `checked` = "I don't
  // have a Passport" ticked, so havePassport is the inverse. Persists the flag +
  // pending status immediately in both toggle directions via onSubmitStep, and
  // resets any uploaded document. The birth-certificate upload (when the company
  // accepts one, no passport) is still captured via the Save button (onFinish).
  const handleTogglePassport = (checked: boolean) => {
    const havePassport = !checked;
    form.setFieldValue('havePassportValue', havePassport);
    form.setFieldValue('documentIdValue', []);
    // Manual verification has no Save button, so this checkbox is the step's
    // only submit. It must be able to finalise the onboarding when addID is the
    // last step, otherwise there is no way to close it.
    if (checked && !isAcceptBirthCertificate) {
      onSubmitStep({
        havePassport,
        verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.PENDING,
      });
      return;
    }
    // Any other toggle only records the flag — the step is still waiting on a
    // birth certificate or a Yoti run, so keep it off the "success" endpoint.
    onSubmitStep(
      {
        havePassport,
        verifiedIdOrCodeStatus: VerifiedIdOrCodeStatusEnum.PENDING,
      },
      { hideToast: true, skipComplete: true },
    );
  };

  // Header action button:
  //  - Has passport                    → "Verify ID" opens the Yoti widget
  //  - No passport + Accept birth cert → "Save" submits the uploaded document
  //  - No passport + Manual verify      → no action (status card only)
  const actions = watchHavePassport
    ? [
        {
          variant: 'outlined' as const,
          name: (yotiPolling
            ? (messages['prospect.onboarding.verifying'] ?? 'Verifying...')
            : (messages['prospect.onboarding.verifyId'] ??
              'Verify ID')) as string,
          onClick: handleVerifyId,
          loading: yotiLoading || yotiPolling,
          // Yoti is a live camera ID check on the employee's own device, so an
          // admin completing the onboarding on their behalf cannot run it.
          // Also locked while a session is being processed, so a second one
          // cannot be started on top of it.
          disabled: disabledVerify || yotiPolling,
        },
      ]
    : isAcceptBirthCertificate
      ? [
          {
            variant: 'outlined' as const,
            name: messages['common.save'] as string,
            onClick: () => form.submit(),
            loading: saving,
            disabled: lockedForAdmin,
          },
        ]
      : [];

  const idStatus: {
    message: string;
    type: 'success' | 'info' | 'warning' | 'error';
    icon?: React.ReactNode;
  } = isVerified
    ? {
        message: messages['prospect.onboarding.verified'] as string,
        type: 'success',
      }
    : backendYotiState === YOTI_ONGOING_STATE
      ? {
          message: (messages['prospect.onboarding.verifying'] ??
            'Verifying...') as string,
          type: 'info',
          icon: <Spin size="small" />,
        }
      : failedYotiState === YOTI_EXPIRED_STATE
        ? {
            message: (messages['prospect.onboarding.yotiExpired'] ??
              'Expired - please re-verify ID') as string,
            type: 'error',
          }
        : failedYotiState === YOTI_ERROR_STATE
          ? {
              message: (messages['prospect.onboarding.yotiError'] ??
                'Error - please re-verify ID') as string,
              type: 'error',
            }
          : {
              message: messages[
                'prospect.onboarding.pendingIDVerification'
              ] as string,
              type: 'warning',
            };

  return (
    <StepCard
      title={messages['prospect.onboarding.addID'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={actions}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          havePassportValue: havePassport,
          documentIdValue: documentCopyBirthcertificate
            ? [
                {
                  name: documentCopyBirthcertificate,
                },
              ]
            : [],
        }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        {!watchHavePassport && isAcceptBirthCertificate ? (
          <Form.Item
            label={
              messages['prospect.onboarding.uploadBirthCertificate'] as string
            }
            name="documentIdValue"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[
              {
                required: true,
                message: messages[
                  'prospect.onboarding.passport.required'
                ] as string,
              },
            ]}
          >
            <Upload.Dragger
              name="files"
              beforeUpload={() => false}
              multiple={false}
              maxCount={1}
              disabled={lockedForAdmin}
              accept=".svg,.png,.jpg,.jpeg,.gif"
            >
              <p className="ant-upload-text">
                {messages['prospect.onboarding.upload.text'] as string}
              </p>
              <p className="ant-upload-hint">
                {messages['prospect.onboarding.upload.hint'] as string}
              </p>
            </Upload.Dragger>
          </Form.Item>
        ) : (
          // Yoti / manual verification / no-passport → show current status.
          <Alert {...idStatus} showIcon className="mb-6" />
        )}
        <Form.Item name="havePassportValue">
          <AppCheckbox
            size="small"
            checked={!watchHavePassport}
            disabled={lockedForAdmin}
            onChange={(e) => handleTogglePassport(e.target.checked)}
          >
            <span className="inline-flex items-center gap-2">
              {messages['prospect.onboarding.noPassport'] as string}
              {submitting && <Spin size="small" />}
            </span>
          </AppCheckbox>
        </Form.Item>
      </AppFormContainer>

      {/* Portalled to body so no transformed ancestor can shrink the overlay
          to its own box. Admins never poll, so they never see it. */}
      {yotiPolling &&
        createPortal(
          <YotiPollingOverlay>
            <div className="relative w-full max-w-[400px] overflow-hidden rounded-xl bg-white text-center shadow-lg">
              {/* Stops polling only — the session stays on the backend, so it
                  is picked up again the next time this step opens. */}
              <button
                type="button"
                aria-label={(messages['common.cancel'] ?? 'Cancel') as string}
                onClick={cancelYotiSession}
                className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-[16px] text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <CloseOutlined />
              </button>
              <div className="flex flex-col items-center gap-4 px-4 py-8 sm:px-6">
                <Spin size="large" />
                {/* One line on every width: the font shrinks on narrow phones
                    instead of wrapping. */}
                <p className="m-0 whitespace-nowrap text-[length:clamp(11px,3.2vw,14px)] font-medium">
                  {
                    (messages['prospect.onboarding.yotiDontClose'] ??
                      'DO NOT CLOSE THE APP WHILE VERIFYING') as string
                  }
                </p>
              </div>
            </div>
          </YotiPollingOverlay>,
          document.body,
        )}

      <AppModalContainer
        isOpen={!!yotiUrl}
        toggle={closeYotiModal}
        title={
          (messages['prospect.onboarding.verifyIdWithPassport'] ??
            'Verify your ID with passport') as string
        }
        width={900}
      >
        {yotiUrl && (
          <iframe
            title="Yoti ID verification"
            src={yotiUrl}
            className="w-full"
            style={{ height: '70vh', border: 'none' }}
            allow="camera; microphone"
          />
        )}
      </AppModalContainer>
    </StepCard>
  );
};

export default AddID;
